type Branch = {
  x: number;
  y: number;
  angle: number;
  length: number;
  targetLen: number;
  width: number;
  gen: number;
  branched: boolean;
  speed: number;
  wobble: number;
  jag: number;
  attach: number;
};

type MountOptions = {
  reducedMotion?: boolean;
  panel?: HTMLElement | null;
};

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function readCssColor(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

type GlyphAnchor = {
  x: number;
  y: number;
  angle: number;
  weight: number;
  lineIdx: number;
};

function wrapLineCharacters(line: HTMLElement): void {
  if (line.dataset.charsReady === '1') return;
  const text = (line.textContent ?? '').replace(/\s+/g, ' ').trim();
  line.textContent = '';
  for (const ch of text) {
    const span = document.createElement('span');
    span.className = 'complex-char';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = ch;
    line.appendChild(span);
  }
  line.dataset.charsReady = '1';
}

function unwrapLineCharacters(line: HTMLElement): void {
  if (line.dataset.charsReady !== '1') return;
  const text = Array.from(line.querySelectorAll('.complex-char'))
    .map((node) => node.textContent ?? '')
    .join('');
  line.textContent = text;
  line.removeAttribute('data-chars-ready');
}

function wrapHeadlineCharacters(headline: HTMLElement): void {
  headline.querySelectorAll<HTMLElement>('.complex-line').forEach(wrapLineCharacters);
}

function unwrapHeadlineCharacters(headline: HTMLElement): void {
  headline.querySelectorAll<HTMLElement>('.complex-line').forEach(unwrapLineCharacters);
}

function angleForEdge(
  edge: 'top' | 'topRight' | 'right',
  rand: () => number,
  lineIdx: number,
): number {
  const lean = lineIdx === 1 ? 0.05 : 0;
  if (edge === 'right') return -0.04 + lean + (rand() - 0.5) * 0.22;
  if (edge === 'topRight') return upRightAngle(rand, 0.85 + lean);
  return -Math.PI / 2 + 0.12 + lean + (rand() - 0.5) * 0.28;
}

function collectGlyphAnchors(
  headline: HTMLElement,
  canvas: HTMLCanvasElement,
  rand: () => number,
): GlyphAnchor[] {
  wrapHeadlineCharacters(headline);
  const canvasBox = canvas.getBoundingClientRect();
  const anchors: GlyphAnchor[] = [];

  headline.querySelectorAll<HTMLElement>('.complex-line').forEach((line, lineIdx) => {
    const chars = line.querySelectorAll<HTMLElement>('.complex-char');
    const lineEmphasis = lineIdx === 1 ? 1.15 : lineIdx === 0 ? 1.05 : 1;

    chars.forEach((el, charIdx) => {
      const r = el.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;

      const ch = (el.textContent ?? '')[0] ?? '';
      const terminal = charIdx === chars.length - 1;
      const hasAscender = /[bdfhklt]/i.test(ch);
      const hasApex = /[avwxk]/i.test(ch);
      const wordStart = charIdx === 0;

      // Sparse exits — word ends, ascenders, apex letters only.
      const eligible = terminal || wordStart || hasAscender || hasApex;
      if (!eligible) return;
      if (!terminal && !hasAscender && rand() > 0.38) return;

      type Pt = { edge: 'top' | 'topRight' | 'right'; x: number; y: number; weight: number };
      const points: Pt[] = [];

      if (terminal || hasApex) {
        points.push({
          edge: 'topRight',
          x: r.right - 0.5,
          y: r.top + Math.max(1, r.height * 0.05),
          weight: (terminal ? 1.2 : 0.78) * lineEmphasis,
        });
      }

      if (hasAscender || terminal || wordStart) {
        points.push({
          edge: 'top',
          x: r.left + r.width * (0.38 + (hasAscender ? 0.14 : 0)),
          y: r.top + 1,
          weight: (hasAscender ? 1 : terminal ? 0.88 : 0.62) * lineEmphasis,
        });
      }

      if (terminal || (wordStart && rand() > 0.4)) {
        points.push({
          edge: 'right',
          x: r.right - 1,
          y: r.top + r.height * (terminal ? 0.34 : 0.48),
          weight: (terminal ? 1.05 : 0.7) * lineEmphasis,
        });
      }

      for (const pt of points) {
        anchors.push({
          x: pt.x - canvasBox.left,
          y: pt.y - canvasBox.top,
          angle: angleForEdge(pt.edge, rand, lineIdx),
          weight: pt.weight,
          lineIdx,
        });
      }
    });
  });

  return anchors;
}

function spawnBranch(
  x: number,
  y: number,
  angle: number,
  targetLen: number,
  width: number,
  gen: number,
  attach: number,
  rand: () => number,
): Branch {
  return {
    x,
    y,
    angle,
    length: 0,
    targetLen,
    width,
    gen,
    branched: false,
    speed: (7 + rand() * 11) * (1 - gen * 0.1),
    wobble: rand() * Math.PI * 2,
    jag: (rand() - 0.5) * 8,
    attach,
  };
}

/** Up-right fan — natural tree lean, not radial math burst. */
function upRightAngle(rand: () => number, spread = 1): number {
  const base = -Math.PI / 4;
  return base + (rand() - 0.5) * 1.05 * spread;
}

/** Max ray distance from a point to the canvas edge in a given direction. */
function rayToBounds(x: number, y: number, angle: number, w: number, h: number): number {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  let best = 0;

  if (Math.abs(cos) > 0.001) {
    const t = cos > 0 ? (w - x) / cos : -x / cos;
    if (t > 0) best = Math.max(best, t);
  }
  if (Math.abs(sin) > 0.001) {
    const t = sin > 0 ? (h - y) / sin : -y / sin;
    if (t > 0) best = Math.max(best, t);
  }

  return best * 0.96;
}

type Point = { x: number; y: number };

/** Two-segment polyline with a single kink — reads angular, not pill-shaped. */
function branchPolyline(b: Branch, time: number): Point[] {
  const len = b.length;
  const progress = Math.min(1, len / Math.max(1, b.targetLen));
  const tipX = b.x + Math.cos(b.angle) * len;
  const tipY = b.y + Math.sin(b.angle) * len;
  const perp = b.angle + Math.PI / 2;
  const sway = Math.sin(time * 0.32 + b.wobble) * (0.35 + b.gen * 0.15);
  const kink = (b.jag + sway) * (0.55 + b.gen * 0.08);
  const joint = 0.38 + (b.gen % 2) * 0.14;
  const midLen = len * joint * progress;
  const midX = b.x + Math.cos(b.angle) * midLen + Math.cos(perp) * kink;
  const midY = b.y + Math.sin(b.angle) * midLen + Math.sin(perp) * kink;

  return [
    { x: b.x, y: b.y },
    { x: midX, y: midY },
    { x: tipX, y: tipY },
  ];
}

function drawAngularBranch(
  ctx: CanvasRenderingContext2D,
  b: Branch,
  time: number,
  color: string,
): void {
  if (b.length < 2) return;

  const pts = branchPolyline(b, time);
  ctx.strokeStyle = color;
  ctx.lineWidth = b.width;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';
  ctx.miterLimit = 14;
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.moveTo(pts[0]!.x, pts[0]!.y);
  for (let i = 1; i < pts.length; i += 1) {
    ctx.lineTo(pts[i]!.x, pts[i]!.y);
  }
  ctx.stroke();
}

type TextMask = { x: number; y: number; w: number; h: number };

function elementTextMask(
  el: HTMLElement,
  canvas: HTMLCanvasElement,
  padX: number,
  padY: number,
): TextMask | null {
  const canvasBox = canvas.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return null;
  return {
    x: r.left - canvasBox.left - padX,
    y: r.top - canvasBox.top - padY,
    w: r.width + padX * 2,
    h: r.height + padY * 2,
  };
}

/** Cut holes in the branch layer wherever DOM text sits — blocks never paint under copy. */
function collectTextExclusionMasks(
  panel: HTMLElement | null,
  canvas: HTMLCanvasElement,
  headline: HTMLElement,
): TextMask[] {
  const masks: TextMask[] = [];

  const h1 = headline.querySelector('h1');
  if (h1) {
    const head = elementTextMask(h1, canvas, 10, 10);
    if (head) masks.push(head);
  }

  if (!panel) return masks;

  panel.querySelectorAll<HTMLElement>('.contact-capabilities li').forEach((li) => {
    const m = elementTextMask(li, canvas, 8, 4);
    if (m) masks.push(m);
  });

  const spotlight = panel.querySelector<HTMLElement>('.capabilities-spotlight');
  if (spotlight) {
    const m = elementTextMask(spotlight, canvas, 10, 5);
    if (m) masks.push(m);
  }

  const eyebrow = panel.querySelector<HTMLElement>('.contact-copy .eyebrow');
  if (eyebrow) {
    const m = elementTextMask(eyebrow, canvas, 8, 5);
    if (m) masks.push(m);
  }

  const support = panel.querySelector<HTMLElement>('.contact-copy .support');
  if (support) {
    const m = elementTextMask(support, canvas, 8, 4);
    if (m) masks.push(m);
  }

  return masks;
}

/** Clip drawing to canvas area outside all protected text blocks. */
function clipOutsideTextBlocks(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  masks: TextMask[],
): void {
  if (masks.length === 0) return;
  ctx.beginPath();
  ctx.rect(0, 0, canvasW, canvasH);
  for (const mask of masks) {
    ctx.rect(mask.x, mask.y, mask.w, mask.h);
  }
  ctx.clip('evenodd');
}

function tipPoint(b: Branch): { x: number; y: number } {
  return {
    x: b.x + Math.cos(b.angle) * b.length,
    y: b.y + Math.sin(b.angle) * b.length,
  };
}

function spawnChildren(parent: Branch, rand: () => number, maxGen: number): Branch[] {
  if (parent.gen >= maxGen || parent.length < parent.targetLen * 0.62) return [];

  const tip = tipPoint(parent);
  const count =
    parent.gen === 0
      ? 2 + Math.floor(rand() * 2)
      : parent.gen < 4
        ? 1 + Math.floor(rand() * 2)
        : parent.gen < maxGen
          ? 1
          : 0;
  if (count === 0) return [];
  const out: Branch[] = [];

  for (let i = 0; i < count; i += 1) {
    const spread = 0.28 + rand() * 0.48;
    const sign = rand() > 0.5 ? 1 : -1;
    const fillBias = (i / Math.max(1, count - 1) - 0.5) * 0.35;
    const angle = parent.angle + sign * spread * (0.62 + rand() * 0.55) + fillBias;
    const targetLen = parent.targetLen * (0.48 + rand() * 0.38);
    out.push(spawnBranch(tip.x, tip.y, angle, targetLen, parent.width, parent.gen + 1, parent.attach, rand));
  }

  if (parent.gen < 3 && rand() > 0.18) {
    out.push(
      spawnBranch(
        tip.x,
        tip.y,
        upRightAngle(rand, 0.65),
        parent.targetLen * (0.38 + rand() * 0.32),
        parent.width,
        parent.gen + 1,
        parent.attach,
        rand,
      ),
    );
  }

  return out;
}

function growAllBranches(branches: Branch[], rand: () => number, maxGen: number): void {
  for (let pass = 0; pass < maxGen + 1; pass += 1) {
    for (const b of branches) {
      b.length = b.targetLen;
      if (!b.branched && b.gen < maxGen) {
        b.branched = true;
        branches.push(...spawnChildren(b, rand, maxGen));
      }
    }
  }
}

/** One limb per glyph anchor — roots sit on letter edges/caps. */
function buildSeedsFromAnchors(
  anchors: GlyphAnchor[],
  rand: () => number,
  stemWidth: number,
  canvasW: number,
  canvasH: number,
): Branch[] {
  const seeds: Branch[] = [];

  for (const anchor of anchors) {
    const w = anchor.weight;
    const reach = rayToBounds(anchor.x, anchor.y, anchor.angle, canvasW, canvasH);
    const len = reach * (0.24 + w * 0.16 + rand() * 0.12);
    seeds.push(
      spawnBranch(
        anchor.x,
        anchor.y,
        anchor.angle + (rand() - 0.5) * 0.12,
        len,
        stemWidth,
        0,
        w,
        rand,
      ),
    );
  }

  return seeds;
}

/** Match Bebas stem/bar weight from rendered glyphs. */
function measureStemWidth(headline: HTMLElement, h1: HTMLElement): number {
  wrapHeadlineCharacters(headline);
  const probes = ['l', 'I', 'i', 't', 'L'];
  for (const probe of probes) {
    for (const el of headline.querySelectorAll<HTMLElement>('.complex-char')) {
      if ((el.textContent ?? '').toLowerCase() !== probe.toLowerCase()) continue;
      const w = el.getBoundingClientRect().width;
      if (w > 1.5) return w;
    }
  }

  const style = getComputedStyle(h1);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const w = ctx.measureText('I').width;
    if (w > 1.5) return w;
  }

  const fs = parseFloat(style.fontSize) || 48;
  return fs * 0.118;
}

export function mountComplexThingsBranches(
  headline: HTMLElement | null,
  options: MountOptions = {},
): () => void {
  if (!headline) return () => undefined;

  const canvas = headline.querySelector<HTMLCanvasElement>('.complex-branches');
  const h1 = headline.querySelector('h1');
  if (!canvas || !h1) return () => undefined;

  const panel = options.panel ?? headline.closest<HTMLElement>('.contact-panel');
  const canvasHome = headline;
  const canvasSibling = h1;

  const reducedMotion = options.reducedMotion ?? false;
  const rand = mulberry32(0x632078);
  const maxGen = reducedMotion ? 3 : 7;
  let branches: Branch[] = [];
  let canvasW = 1;
  let canvasH = 1;
  let raf = 0;
  let running = true;
  let lastTs = 0;
  let t = 0;

  const resize = (): void => {
    const host = panel ?? headline;
    const hostRect = host.getBoundingClientRect();
    canvasW = hostRect.width;
    canvasH = hostRect.height;

    if (panel && canvas.parentElement !== panel) {
      panel.appendChild(canvas);
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.ceil(canvasW * dpr);
    canvas.height = Math.ceil(canvasH * dpr);
    canvas.style.width = `${canvasW}px`;
    canvas.style.height = `${canvasH}px`;
    canvas.style.left = '0';
    canvas.style.top = '0';

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const anchors = collectGlyphAnchors(headline, canvas, rand);
    const stemWidth = measureStemWidth(headline, h1);
    branches = buildSeedsFromAnchors(anchors, rand, stemWidth, canvasW, canvasH);

    if (reducedMotion) {
      growAllBranches(branches, rand, maxGen);
    }
  };

  const tick = (now: number): void => {
    if (!running) return;
    raf = requestAnimationFrame(tick);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dt = lastTs > 0 ? Math.min(0.05, (now - lastTs) / 1000) : 0.016;
    lastTs = now;
    t += dt;
    const motion = reducedMotion ? 0 : 1;

    if (motion > 0) {
      for (const b of branches) {
        if (b.length < b.targetLen) {
          b.length = Math.min(b.targetLen, b.length + b.speed * dt);
        }
        const forkAt = 0.52 + (b.gen % 3) * 0.04;
        if (b.length >= b.targetLen * forkAt && !b.branched) {
          b.branched = true;
          branches.push(...spawnChildren(b, rand, maxGen));
        }
      }
    }

    const w = canvas.width / Math.min(window.devicePixelRatio || 1, 2);
    const h = canvas.height / Math.min(window.devicePixelRatio || 1, 2);
    ctx.clearRect(0, 0, w, h);
    ctx.save();
    const textMasks = collectTextExclusionMasks(panel, canvas, headline);
    clipOutsideTextBlocks(ctx, w, h, textMasks);

    const ink = readCssColor('--text', '#1a1a1a');
    const sorted = [...branches].sort((a, b) => a.gen - b.gen);

    for (const b of sorted) {
      drawAngularBranch(ctx, b, t, ink);
    }

    ctx.restore();
  };

  resize();
  void document.fonts.ready.then(() => {
    if (running) resize();
  });
  const ro = new ResizeObserver(() => resize());
  ro.observe(panel ?? headline);
  window.addEventListener('resize', resize);
  raf = requestAnimationFrame(tick);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    ro.disconnect();
    window.removeEventListener('resize', resize);
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (canvas.parentElement !== canvasHome) {
      canvasHome.insertBefore(canvas, canvasSibling);
    }
    canvas.removeAttribute('style');
    unwrapHeadlineCharacters(headline);
  };
}
